import React from 'react';
import { useSession } from '../context/Session';
import { Icon } from './ui';

// Port of the app's lib/widgets/badge_medal.dart: a metallic rim, a bevelled
// inner ring and a glossy, domed face with the badge icon on it. Each badge
// tone gets its own silhouette; locked badges render as a flat grey medal with
// a small lock chip. Layers are stacked divs cut with clip-path.

const SHAPES = { workout: 'rosette', meal: 'coin', water: 'coin', body: 'hexagon', master: 'starburst' };

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

/** Shifts the HSL lightness of an [r, g, b] color by `amount` (negative darkens). */
function shade(rgb, amount) {
  const [h, s, l] = rgbToHsl(rgb);
  return { h, s, l: Math.min(1, Math.max(0, l + amount)) };
}

const hsla = ({ h, s, l }, a = 1) => `hsla(${h.toFixed(1)}, ${(s * 100).toFixed(1)}%, ${(l * 100).toFixed(1)}%, ${a})`;

/** Flutter's ThemeData.estimateBrightnessForColor. */
function isLightColor(rgb) {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
  return (lum + 0.05) * (lum + 0.05) > 0.15;
}

function polygon(points) {
  return `polygon(${points.map(([x, y]) => `${x.toFixed(2)}px ${y.toFixed(2)}px`).join(', ')})`;
}

function regular(c, r, sides) {
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / sides;
    pts.push([c + Math.cos(a) * r, c + Math.sin(a) * r]);
  }
  return polygon(pts);
}

function polar(c, radius, samples = 180) {
  const pts = [];
  for (let i = 0; i < samples; i++) {
    const t = (i * 2 * Math.PI) / samples;
    const rr = radius(t);
    pts.push([c + Math.cos(t) * rr, c + Math.sin(t) * rr]);
  }
  return polygon(pts);
}

function outerClip(shape, c, r) {
  switch (shape) {
    case 'hexagon':
      return regular(c, r, 6);
    case 'rosette':
      // Scalloped edge: 14 rounded lobes.
      return polar(c, (t) => r * (0.93 + 0.07 * Math.cos(14 * t)));
    case 'starburst':
      // 12 sharp points, like a ribbon rosette.
      return polar(c, (t) => r * (0.84 + 0.16 * Math.abs(((t * 12) / (2 * Math.PI)) % 1 - 0.5) * 2), 240);
    default:
      return `circle(${r}px at 50% 50%)`;
  }
}

function innerClip(shape, c, r) {
  return shape === 'hexagon' ? regular(c, r, 6) : `circle(${r}px at 50% 50%)`;
}

const layer = (clipPath, background) => ({ position: 'absolute', inset: 0, clipPath, background });

export default function BadgeMedal({ badge, earned, size = 64, count = 1 }) {
  const { isDark } = useSession();
  // Locked: the app's lerp(surface, onSurface, 0.32) on a white / black surface.
  const baseRgb = earned ? hexToRgb(badge.color) : isDark ? [82, 82, 82] : [173, 173, 173];
  const shape = SHAPES[badge.tone] || 'coin';
  const c = size / 2;
  const r = size / 2;

  const base = shade(baseRgb, 0);
  const light = shade(baseRgb, 0.22);
  const lighter = shade(baseRgb, 0.38);
  const dark = shade(baseRgb, -0.2);
  const darker = shade(baseRgb, -0.32);
  const iconColor = isLightColor(baseRgb) ? hsla(shade(baseRgb, -0.38), earned ? 1 : 0.55) : `rgba(255,255,255,${earned ? 1 : 0.55})`;

  const sweep = (earned ? [lighter, base, dark, base, lighter, base, dark, base, lighter] : [light, base, dark, base, light]).map((x) => hsla(x)).join(', ');
  const faceR = r * 0.66;
  const face = `radial-gradient(circle ${faceR * 2 * 1.1}px at ${c - 0.35 * faceR}px ${c - 0.45 * faceR}px, ${hsla(light)} 0%, ${hsla(base)} 55%, ${hsla(dark)} 100%)`;

  return (
    <div className="gm-medal" style={{ width: size, height: size }} aria-hidden>
      {/* Rim, with a thin darker edge and a drop shadow when earned. */}
      <div style={{ position: 'absolute', inset: 0, filter: earned ? `drop-shadow(0 ${r * 0.04}px ${r * 0.08}px ${hsla(darker, 0.6)})` : undefined }}>
        <div style={layer(outerClip(shape, c, r * 0.9725), hsla(darker, 0.5))} />
        <div style={layer(outerClip(shape, c, r * 0.9475), `conic-gradient(from 45deg, ${sweep})`)} />
      </div>
      {/* Bevel: lit from the opposite side, so the face looks recessed. */}
      <div style={layer(innerClip(shape, c, r * 0.74), `linear-gradient(135deg, ${hsla(darker)}, ${hsla(light)})`)} />
      <div style={layer(innerClip(shape, c, r * 0.675), `rgba(255,255,255,${earned ? 0.35 : 0.2})`)} />
      <div style={layer(innerClip(shape, c, r * 0.645), face)} />
      {earned && (
        <>
          <div style={layer(outerClip(shape, c, r * 0.96), 'none')}>
            <div
              style={{
                position: 'absolute',
                left: c - r * 1.1,
                top: c - r * 1.25,
                width: r * 2.2,
                height: r * 1.35,
                borderRadius: '50%',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.32), rgba(255,255,255,0))',
              }}
            />
          </div>
          <span className="gm-medal__glint" style={{ left: c - r * 0.56 - r * 0.045, top: c - r * 0.56 - r * 0.045, width: r * 0.09, height: r * 0.09 }} />
          <span className="gm-medal__glint" style={{ left: c - r * 0.44 - r * 0.025, top: c - r * 0.68 - r * 0.025, width: r * 0.05, height: r * 0.05 }} />
        </>
      )}
      <Icon
        name={badge.icon}
        className="gm-medal__icon"
        size={size * (shape === 'hexagon' ? 0.36 : 0.38)}
        style={{ color: iconColor, textShadow: earned ? `0 ${size * 0.015}px ${size * 0.03}px ${hsla(shade(baseRgb, -0.3), 0.7)}` : undefined }}
      />
      {!earned && (
        <span className="gm-medal__lock" style={{ width: size * 0.34, height: size * 0.34 }}>
          <Icon name="lock" size={size * 0.2} />
        </span>
      )}
      {earned && count > 1 && (
        <span
          className="gm-medal__count"
          style={{
            right: -size * 0.06,
            top: -size * 0.04,
            minWidth: size * 0.36,
            padding: `${size * 0.02}px ${size * 0.06}px`,
            fontSize: Math.min(16, Math.max(9, size * 0.19)),
            background: hsla(shade(baseRgb, -0.22)),
          }}
        >
          ×{count}
        </span>
      )}
    </div>
  );
}
